import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { DealRoom, DealRoomTopNodes } from 'models/DealRoom';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DraggableCard from 'components/DraggableCard';
import DragIcon from 'icons/Drag.icon';
import { Button, Loader } from 'ncoded-component-library';
import MymeTypeIcon from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/components/MymeTypeIcon';
import { getIsFileReorderViewModeFile, getNodeMimeType } from 'utils';
import useNodeInfo from 'components/FileAccessSelector/hooks/useNodeInfo';
import DataRoomNodeTagAndVersion from 'components/DataRoomNodeTagAndVersion';
import api from 'api';
import RadioButtonField from 'components/RadioGroupField/RadioButtonField';
import useReordering from './hooks/useReordering';
import DocumentViewContext from 'providers/DocumentView/DocumentView.context';
import DataRoomNodeCurrentLocation from 'components/DataRoomNodeCurrentLocation';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { FILE_REORDER_VIEW_MODE, FileReorderViewMode } from 'models/User';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { Q_AND_A_SIDES } from 'router/subrouters/DataRoom/models/QAndA';
import { getInitialFileReorderViewMode } from '../../utils';
import DisplayFilesPreview from './components/DisplayFilesPreview';

import './ReorderFilesDrawer.styles.scss';

type ReorderFilesForm = {
  fileReorderViewMode: FileReorderViewMode;
};

type ReorderFilesDrawerProps = {
  dealRoom: DealRoom;
  dealRoomTopNodes: DealRoomTopNodes[];
  dealRoomStarredNodes: DealRoomTopNodes[];
  onClose: (
    dataRoomTopNodesAfterReorder: DealRoomTopNodes[],
    dealRoomStarredNodes: DealRoomTopNodes[],
  ) => void;
} & SideDrawerProps;

const ReorderFilesDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ReorderFilesDrawerProps
> = (props, ref) => {
  const {
    children,
    className,
    dealRoom,
    dealRoomTopNodes,
    dealRoomStarredNodes,
    onClose,
    ...rest
  } = props;

  const { _id: dealRoomId, name, userId, buyersSort } = dealRoom;

  const {
    currentUser: { fileReorderViewMode },
    setCurrentUser,
  } = useContext(CurrentUserContext);
  const { openNode } = useContext(DocumentViewContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const { hasStarredNodes, items, resetItems, onDragEnd } = useReordering(
    dealRoomTopNodes,
    dealRoomStarredNodes,
  );

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>();

  useImperativeHandle(ref, () => drawer, [drawer]);

  const classes = classNames('reorder-files-drawer', className);

  const { t } = useTranslation();

  const createInfoData = useNodeInfo(false);

  const dataRoomTopNodesAfterReorder = useMemo(
    () =>
      items.map((dataRoomNode, index) => ({
        dataRoomNode,
        orderNumber: index,
        dataRoomNodeId: dataRoomNode._id,
      })),
    [items],
  );

  const initialValues = useMemo(
    () => ({
      fileReorderViewMode: getInitialFileReorderViewMode(
        fileReorderViewMode,
        buyersSort,
        dataRoomTopNodesAfterReorder,
      ),
    }),
    [buyersSort, dataRoomTopNodesAfterReorder, fileReorderViewMode],
  );

  const closeAction = useCallback(() => {
    drawer?.setOpen(false);
    resetItems();
  }, [drawer, resetItems]);

  const handleOnSubmit = useCallback(
    async (values: ReorderFilesForm) => {
      const { fileReorderViewMode } = values;

      try {
        const newOrderForApi = items.map(({ _id }, index) => ({
          dataRoomNodeId: _id,
          orderNumber: index,
        }));

        await api.portfolio.seller.updateView(dealRoomId, {
          fileReorderViewMode,
          ...(getIsFileReorderViewModeFile(fileReorderViewMode)
            ? hasStarredNodes
              ? { dataRoomStarredNodes: newOrderForApi }
              : { dataRoomTopNodes: newOrderForApi }
            : {}),
        });

        setCurrentUser((old) => ({ ...old, fileReorderViewMode }));

        onClose(
          hasStarredNodes ? dealRoomTopNodes : dataRoomTopNodesAfterReorder,
          hasStarredNodes ? dataRoomTopNodesAfterReorder : dealRoomStarredNodes,
        );
        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      }
    },
    [
      items,
      dealRoomId,
      hasStarredNodes,
      dealRoomTopNodes,
      dataRoomTopNodesAfterReorder,
      dealRoomStarredNodes,
      drawer,
      onClose,
      setCurrentUser,
      popServerError,
    ],
  );

  const renderSelectView = useCallback(
    (viewMode: FileReorderViewMode, title: string, description: string) => (
      <Field<string>
        name="fileReorderViewMode"
        type="radio"
        value={viewMode}
        label={
          <span className="reorder-files-drawer__radio">
            <span>{title}</span>
            <span className="reorder-files-drawer__radio__description">
              {description}
            </span>
          </span>
        }
        component={RadioButtonField}
      />
    ),
    [],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      closeAction={closeAction}
      className={classes}
      {...rest}
    >
      <div className="header">
        <p>{t('configureFileAccess')}</p>
        <p>{t('chooseHowYouWantToManageFileAccess')}</p>
      </div>
      <Form
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        render={({
          submitting,
          values: { fileReorderViewMode },
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            {submitting && <Loader />}
            <div className="reorder-files-drawer__view-options">
              {renderSelectView(
                FILE_REORDER_VIEW_MODE.LEAD,
                t('byPermissionCategory'),
                t('viewSummaryByLead'),
              )}

              {renderSelectView(
                FILE_REORDER_VIEW_MODE.FILE,
                t('byFile'),
                t('viewSummaryByFile'),
              )}

              <DisplayFilesPreview />
            </div>
            {getIsFileReorderViewModeFile(fileReorderViewMode) && (
              <>
                <div className="border" />
                <div className="subheader">
                  <p>{t('reorderFiles')}</p>
                  <p>
                    {t(
                      hasStarredNodes
                        ? 'reorderStarredFilesDisplayedWithingLeadInformation'
                        : 'reorderFilesDisplayedWithingLeadInformation',
                    )}
                  </p>
                </div>
                <div className="content">
                  <DndContext
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={items}
                      strategy={verticalListSortingStrategy}
                    >
                      {items.map((node) => (
                        <DraggableCard key={node._id} id={node._id}>
                          {({ attributes, listeners, setActivatorNodeRef }) => (
                            <div className="draggable">
                              <div
                                className="draggable__left"
                                ref={setActivatorNodeRef}
                                {...attributes}
                                {...listeners}
                              >
                                <Button icon={DragIcon} variant="icon" />
                              </div>

                              <div
                                className="draggable__right clickable"
                                onClick={() =>
                                  openNode(node, {
                                    dataRoomName: name,
                                    ownerId: userId,
                                    userSide: Q_AND_A_SIDES.SELL,
                                    dealRoomId,
                                  })
                                }
                              >
                                <div>
                                  <MymeTypeIcon
                                    mimeType={getNodeMimeType(node)}
                                  />

                                  <div className="node-wrapper">
                                    <div>
                                      <p className="name">
                                        <span>{node.name}</span>
                                        <DataRoomNodeTagAndVersion
                                          tag={node.tag}
                                          version={node.version}
                                        />
                                      </p>

                                      {createInfoData(node)}
                                    </div>
                                    <DataRoomNodeCurrentLocation
                                      dataRoomId={node.dataRoomId}
                                      currentLocation={{
                                        parentId: node.parentId || null,
                                        parentName: node.parentName,
                                      }}
                                      onLinkClick={() => drawer?.setOpen(false)}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </DraggableCard>
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
                <div className="border" />
              </>
            )}

            <div className="actions">
              <Button variant="link" onClick={closeAction}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={submitting}>
                {t('save')}
              </Button>
            </div>
          </form>
        )}
      />
    </SideDrawer>
  );
};

export default forwardRef(ReorderFilesDrawer);
